import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Tabs,
  Tab,
  Button,
  Grid,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  RadioGroup,
  FormControlLabel,
  Radio,
  CircularProgress,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format, startOfMonth, endOfMonth, getDaysInMonth, setDate, isSameMonth, isAfter, getDate } from 'date-fns';
import {
  useGetEmployeesQuery,
  useGetAttendanceQuery,
  useMarkDailyBulkMutation,
  useMarkMonthlyBulkMutation,
} from '../../../store/api/hrmsApi';

// Reusable sub-components for tabs
function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

function AttendanceDashboard() {
  const [tabValue, setTabValue] = useState(0);

  // Common State
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedMonth, setSelectedMonth] = useState(new Date());
  const [selectedEmployeeId, setSelectedEmployeeId] = useState('');

  // Queries
  const { data: employeesData, isLoading: isLoadingEmployees } = useGetEmployeesQuery({ limit: 1000 });
  const employees = employeesData?.employees || [];

  const [markDailyBulk, { isLoading: isMarkingDaily }] = useMarkDailyBulkMutation();
  const [markMonthlyBulk, { isLoading: isMarkingMonthly }] = useMarkMonthlyBulkMutation();

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  return (
    <Box>
      <Typography variant="h4" sx={{ fontWeight: 600, mb: 3 }}>
        Attendance Tracking
      </Typography>

      <Card>
        <CardContent>
          <Tabs value={tabValue} onChange={handleTabChange} variant="scrollable" scrollButtons="auto">
            <Tab label="Daily (All Employees)" />
            <Tab label="Monthly (All Employees)" />
            <Tab label="Daily (Single Employee)" />
            <Tab label="Monthly (Single Employee)" />
          </Tabs>

          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <TabPanel value={tabValue} index={0}>
              <DailyAllEmployees
                date={selectedDate}
                setDate={setSelectedDate}
                employees={employees}
                markDailyBulk={markDailyBulk}
                isSaving={isMarkingDaily}
              />
            </TabPanel>

            <TabPanel value={tabValue} index={1}>
              <MonthlyAllEmployees
                month={selectedMonth}
                setMonth={setSelectedMonth}
                employees={employees}
                markMonthlyBulk={markMonthlyBulk}
                isSaving={isMarkingMonthly}
              />
            </TabPanel>

            <TabPanel value={tabValue} index={2}>
              <DailySingleEmployee
                date={selectedDate}
                setDate={setSelectedDate}
                employeeId={selectedEmployeeId}
                setEmployeeId={setSelectedEmployeeId}
                employees={employees}
                markDailyBulk={markDailyBulk}
                isSaving={isMarkingDaily}
              />
            </TabPanel>

            <TabPanel value={tabValue} index={3}>
              <MonthlySingleEmployee
                month={selectedMonth}
                setMonth={setSelectedMonth}
                employeeId={selectedEmployeeId}
                setEmployeeId={setSelectedEmployeeId}
                employees={employees}
                markMonthlyBulk={markMonthlyBulk}
                isSaving={isMarkingMonthly}
              />
            </TabPanel>
          </LocalizationProvider>
        </CardContent>
      </Card>
    </Box>
  );
}

// -------------------------------------------------------------
// Mode 1: Daily (All Employees)
// -------------------------------------------------------------
function DailyAllEmployees({ date, setDate, employees, markDailyBulk, isSaving }) {
  const formattedDate = format(date, 'yyyy-MM-dd');
  const { data, isLoading } = useGetAttendanceQuery({
    startDate: formattedDate,
    endDate: formattedDate,
  });

  const [attendanceState, setAttendanceState] = useState({});

  useEffect(() => {
    if (data && data.attendance && employees.length > 0) {
      const state = {};
      employees.forEach((emp) => {
        const record = data.attendance.find((a) => a.employeeId === emp.id);
        state[emp.id] = record ? record.status : 'Present'; // Default to Present
      });
      setAttendanceState(state);
    }
  }, [data, employees]);

  const handleStatusChange = (employeeId, status) => {
    setAttendanceState((prev) => ({ ...prev, [employeeId]: status }));
  };

  const handleSave = async () => {
    const records = Object.keys(attendanceState).map((empId) => ({
      employeeId: empId,
      status: attendanceState[empId],
    }));
    try {
      await markDailyBulk({ date: formattedDate, records }).unwrap();
      alert('Daily attendance saved successfully!');
    } catch (e) {
      alert('Failed to save attendance.');
    }
  };

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
        <DatePicker
          label="Select Date"
          value={date}
          onChange={(newDate) => setDate(newDate || new Date())}
          slotProps={{ textField: { size: 'small' } }}
        />
        <Button variant="contained" onClick={handleSave} disabled={isSaving}>
          {isSaving ? 'Saving...' : 'Save Attendance'}
        </Button>
      </Box>

      {isLoading ? (
        <CircularProgress />
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.50' }}>
                <TableCell sx={{ fontWeight: 600 }}>Employee</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {employees.map((emp) => (
                <TableRow key={emp.id}>
                  <TableCell>
                    {emp.firstName} {emp.lastName}
                  </TableCell>
                  <TableCell>
                    <RadioGroup
                      row
                      value={attendanceState[emp.id] || 'Present'}
                      onChange={(e) => handleStatusChange(emp.id, e.target.value)}
                    >
                      <FormControlLabel value="Present" control={<Radio size="small" color="success" />} label="Present" />
                      <FormControlLabel value="Absent" control={<Radio size="small" color="error" />} label="Absent" />
                      <FormControlLabel value="Half Day" control={<Radio size="small" color="warning" />} label="Half Day" />
                      <FormControlLabel value="Leave" control={<Radio size="small" color="info" />} label="Leave" />
                    </RadioGroup>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}

// -------------------------------------------------------------
// Mode 2: Monthly (All Employees)
// -------------------------------------------------------------
function MonthlyAllEmployees({ month, setMonth, employees, markMonthlyBulk, isSaving }) {
  const start = startOfMonth(month);
  const end = endOfMonth(month);
  
  const now = new Date();
  let daysToRender = getDaysInMonth(month);
  if (isSameMonth(month, now)) {
    daysToRender = getDate(now);
  } else if (isAfter(month, now)) {
    daysToRender = 0;
  }
  
  const { data, isLoading } = useGetAttendanceQuery({
    startDate: format(start, 'yyyy-MM-dd'),
    endDate: format(end, 'yyyy-MM-dd'),
  });

  const [attendanceState, setAttendanceState] = useState({});

  useEffect(() => {
    if (data && data.attendance && employees.length > 0) {
      const state = {};
      const start = startOfMonth(month);
      const daysInMonth = getDaysInMonth(month);
      // Initialize everyone to Present
      employees.forEach((emp) => {
        state[emp.id] = {};
        for (let i = 1; i <= daysToRender; i++) {
          const currentDate = format(setDate(start, i), 'yyyy-MM-dd');
          state[emp.id][currentDate] = 'Present';
        }
      });
      // Override with DB data
      data.attendance.forEach((record) => {
        if (state[record.employeeId]) {
          state[record.employeeId][record.date] = record.status;
        }
      });
      setAttendanceState(state);
    }
  }, [data, employees, month]);

  const toggleStatus = (empId, dateString) => {
    const current = attendanceState[empId]?.[dateString];
    let next = 'Present';
    if (current === 'Present') next = 'Absent';
    else if (current === 'Absent') next = 'Half Day';
    else if (current === 'Half Day') next = 'Leave';
    else next = 'Present';

    setAttendanceState((prev) => ({
      ...prev,
      [empId]: {
        ...prev[empId],
        [dateString]: next,
      },
    }));
  };

  const getCellColor = (status) => {
    if (status === 'Absent') return 'error.light';
    if (status === 'Half Day') return 'warning.light';
    if (status === 'Leave') return 'info.light';
    return 'success.light'; // Present
  };

  const getCellLabel = (status) => {
    if (status === 'Absent') return 'A';
    if (status === 'Half Day') return 'H';
    if (status === 'Leave') return 'L';
    return 'P'; // Present
  };

  const handleSave = async () => {
    const records = [];
    Object.keys(attendanceState).forEach((empId) => {
      Object.keys(attendanceState[empId]).forEach((dateStr) => {
        records.push({
          employeeId: empId,
          date: dateStr,
          status: attendanceState[empId][dateStr],
        });
      });
    });

    try {
      await markMonthlyBulk({ records }).unwrap();
      alert('Monthly attendance saved successfully!');
    } catch (e) {
      alert('Failed to save attendance.');
    }
  };

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
        <DatePicker
          views={['year', 'month']}
          label="Select Month"
          value={month}
          onChange={(newMonth) => setMonth(newMonth || new Date())}
          slotProps={{ textField: { size: 'small' } }}
        />
        <Button variant="contained" onClick={handleSave} disabled={isSaving}>
          {isSaving ? 'Saving...' : 'Save Attendance'}
        </Button>
        <Typography variant="body2" color="text.secondary">
          (Click cells to toggle: P {'->'} A {'->'} H {'->'} L)
        </Typography>
      </Box>

      {isLoading ? (
        <CircularProgress />
      ) : (
        <TableContainer sx={{ maxHeight: 600 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ minWidth: 150, fontWeight: 600, bgcolor: 'grey.100' }}>
                  Employee
                </TableCell>
                {Array.from({ length: daysToRender }, (_, i) => (
                  <TableCell key={i} align="center" sx={{ px: 0.5, bgcolor: 'grey.100' }}>
                    {i + 1}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {employees.map((emp) => (
                <TableRow key={emp.id} hover>
                  <TableCell>{emp.firstName}</TableCell>
                  {Array.from({ length: daysToRender }, (_, i) => {
                    const dateStr = format(setDate(start, i + 1), 'yyyy-MM-dd');
                    const status = attendanceState[emp.id]?.[dateStr] || 'Present';
                    return (
                      <TableCell
                        key={i}
                        align="center"
                        sx={{
                          px: 0,
                          cursor: 'pointer',
                          bgcolor: getCellColor(status),
                          color: 'black',
                          borderRight: '1px solid #e0e0e0',
                          userSelect: 'none',
                        }}
                        onClick={() => toggleStatus(emp.id, dateStr)}
                      >
                        {getCellLabel(status)}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}

// -------------------------------------------------------------
// Mode 3: Daily (Single Employee)
// -------------------------------------------------------------
function DailySingleEmployee({ date, setDate, employeeId, setEmployeeId, employees, markDailyBulk, isSaving }) {
  const formattedDate = format(date, 'yyyy-MM-dd');
  const { data, isLoading } = useGetAttendanceQuery(
    { startDate: formattedDate, endDate: formattedDate, employeeId },
    { skip: !employeeId }
  );

  const [status, setStatus] = useState('Present');

  useEffect(() => {
    if (data && data.attendance && data.attendance.length > 0) {
      setStatus(data.attendance[0].status);
    } else {
      setStatus('Present');
    }
  }, [data, employeeId, formattedDate]);

  const handleSave = async () => {
    if (!employeeId) return alert('Select an employee');
    try {
      await markDailyBulk({
        date: formattedDate,
        records: [{ employeeId, status }],
      }).unwrap();
      alert('Attendance saved successfully!');
    } catch (e) {
      alert('Failed to save attendance.');
    }
  };

  return (
    <Box sx={{ maxWidth: 500 }}>
      <Grid container spacing={3}>
        <Grid size={{xs: 12, sm: 6}}>
          <FormControl fullWidth size="small">
            <InputLabel>Employee</InputLabel>
            <Select
              value={employeeId}
              label="Employee"
              onChange={(e) => setEmployeeId(e.target.value)}
            >
              {employees.map((emp) => (
                <MenuItem key={emp.id} value={emp.id}>
                  {emp.firstName} {emp.lastName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid size={{xs: 12, sm: 6}}>
          <DatePicker
            label="Date"
            value={date}
            onChange={(newDate) => setDate(newDate || new Date())}
            slotProps={{ textField: { fullWidth: true, size: 'small' } }}
          />
        </Grid>
        {employeeId && (
          <Grid size={{xs: 12}}>
            {isLoading ? (
              <CircularProgress size={24} />
            ) : (
              <FormControl fullWidth>
                <Typography variant="subtitle2" gutterBottom>Status</Typography>
                <RadioGroup row value={status} onChange={(e) => setStatus(e.target.value)}>
                  <FormControlLabel value="Present" control={<Radio color="success" />} label="Present" />
                  <FormControlLabel value="Absent" control={<Radio color="error" />} label="Absent" />
                  <FormControlLabel value="Half Day" control={<Radio color="warning" />} label="Half Day" />
                  <FormControlLabel value="Leave" control={<Radio color="info" />} label="Leave" />
                </RadioGroup>
              </FormControl>
            )}
          </Grid>
        )}
        <Grid size={{xs: 12}}>
          <Button variant="contained" onClick={handleSave} disabled={isSaving || !employeeId}>
            {isSaving ? 'Saving...' : 'Save'}
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
}

// -------------------------------------------------------------
// Mode 4: Monthly (Single Employee)
// -------------------------------------------------------------
function MonthlySingleEmployee({ month, setMonth, employeeId, setEmployeeId, employees, markMonthlyBulk, isSaving }) {
  const start = startOfMonth(month);
  const end = endOfMonth(month);
  
  const now = new Date();
  let daysToRender = getDaysInMonth(month);
  if (isSameMonth(month, now)) {
    daysToRender = getDate(now);
  } else if (isAfter(month, now)) {
    daysToRender = 0;
  }
  
  const { data, isLoading } = useGetAttendanceQuery(
    {
      startDate: format(start, 'yyyy-MM-dd'),
      endDate: format(end, 'yyyy-MM-dd'),
      employeeId,
    },
    { skip: !employeeId }
  );

  const [attendanceState, setAttendanceState] = useState({});

  useEffect(() => {
    if (employeeId) {
      const state = {};
      const start = startOfMonth(month);
      // Default all to present
      for (let i = 1; i <= daysToRender; i++) {
        const currentDate = format(setDate(start, i), 'yyyy-MM-dd');
        state[currentDate] = 'Present';
      }
      if (data && data.attendance) {
        data.attendance.forEach((record) => {
          state[record.date] = record.status;
        });
      }
      setAttendanceState(state);
    }
  }, [data, employeeId, month]);

  const handleStatusChange = (dateStr, status) => {
    setAttendanceState((prev) => ({ ...prev, [dateStr]: status }));
  };

  const handleSave = async () => {
    if (!employeeId) return;
    const records = Object.keys(attendanceState).map((dateStr) => ({
      employeeId,
      date: dateStr,
      status: attendanceState[dateStr],
    }));

    try {
      await markMonthlyBulk({ records }).unwrap();
      alert('Monthly attendance saved successfully!');
    } catch (e) {
      alert('Failed to save attendance.');
    }
  };

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Employee</InputLabel>
          <Select
            value={employeeId}
            label="Employee"
            onChange={(e) => setEmployeeId(e.target.value)}
          >
            {employees.map((emp) => (
              <MenuItem key={emp.id} value={emp.id}>
                {emp.firstName} {emp.lastName}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <DatePicker
          views={['year', 'month']}
          label="Month"
          value={month}
          onChange={(newMonth) => setMonth(newMonth || new Date())}
          slotProps={{ textField: { size: 'small' } }}
        />
        <Button variant="contained" onClick={handleSave} disabled={isSaving || !employeeId}>
          {isSaving ? 'Saving...' : 'Save Attendance'}
        </Button>
      </Box>

      {employeeId && (
        isLoading ? (
          <CircularProgress />
        ) : (
          <TableContainer sx={{ maxWidth: 600 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {Array.from({ length: daysToRender }, (_, i) => {
                  const dateObj = setDate(start, i + 1);
                  const dateStr = format(dateObj, 'yyyy-MM-dd');
                  return (
                    <TableRow key={i}>
                      <TableCell>{format(dateObj, 'MMM dd, yyyy (EEE)')}</TableCell>
                      <TableCell>
                        <RadioGroup
                          row
                          value={attendanceState[dateStr] || 'Present'}
                          onChange={(e) => handleStatusChange(dateStr, e.target.value)}
                        >
                          <FormControlLabel value="Present" control={<Radio size="small" color="success" />} label="P" />
                          <FormControlLabel value="Absent" control={<Radio size="small" color="error" />} label="A" />
                          <FormControlLabel value="Half Day" control={<Radio size="small" color="warning" />} label="H" />
                          <FormControlLabel value="Leave" control={<Radio size="small" color="info" />} label="L" />
                        </RadioGroup>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )
      )}
    </Box>
  );
}

export default AttendanceDashboard;
